import { useCallback, useEffect, useState } from 'react';
import { COLUMN_GAP, COLUMN_WIDTH } from './types';

/**
 * Horizontal scrolling for a Kanban board: eased shift-wheel, and
 * Cmd/Ctrl+Arrow to step one column.
 *
 * Returns a ref *callback* rather than a ref object on purpose. The legacy job
 * board captures `ref.current` when its effect runs with an empty dependency
 * array, but the scroll container does not exist on the first commit — the
 * board early-returns a spinner while its data loads — so the wheel listener
 * binds to nothing and shift-wheel is silently dead on a cold load. Keying the
 * effect on the node fixes that.
 */
export function useKanbanScroll(opts?: { step?: number }) {
  const [scrollNode, setNode] = useState<HTMLDivElement | null>(null);
  const step = opts?.step ?? COLUMN_WIDTH + COLUMN_GAP;

  const setScrollNode = useCallback((node: HTMLDivElement | null) => setNode(node), []);

  // Eased horizontal wheel scrolling.
  useEffect(() => {
    if (!scrollNode) return;

    let target = scrollNode.scrollLeft;
    let frame: number | null = null;

    const settle = () => {
      const diff = target - scrollNode.scrollLeft;
      if (Math.abs(diff) <= 1) {
        scrollNode.scrollLeft = target;
        frame = null;
        return;
      }
      scrollNode.scrollLeft += diff * 0.15;
      frame = requestAnimationFrame(settle);
    };

    const onWheel = (event: WheelEvent) => {
      // A trackpad's horizontal swipe already scrolls horizontally; only take
      // over when the gesture is meant for us.
      if (!event.shiftKey && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      const delta = event.shiftKey ? event.deltaY : event.deltaX;
      const max = scrollNode.scrollWidth - scrollNode.clientWidth;
      const next = Math.max(0, Math.min(max, target + delta));
      if (next === target) return;

      event.preventDefault();
      target = next;
      if (frame === null) frame = requestAnimationFrame(settle);
    };

    // Not passive: we call preventDefault to stop the page scrolling too.
    scrollNode.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      scrollNode.removeEventListener('wheel', onWheel);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [scrollNode]);

  // Cmd/Ctrl + Arrow steps exactly one column.
  useEffect(() => {
    if (!scrollNode) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      // Bound to window rather than the container, because a plain scrolling
      // div is not focusable by default and focus sits on <body> after a normal
      // load — a container-scoped listener would simply never fire. This guard
      // is what keeps two mounted boards from both scrolling.
      const active = document.activeElement;
      const engaged =
        (active instanceof Node && scrollNode.contains(active)) || scrollNode.matches(':hover');
      if (!engaged) return;

      event.preventDefault();
      scrollNode.scrollBy({
        left: event.key === 'ArrowRight' ? step : -step,
        behavior: 'auto',
      });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [scrollNode, step]);

  return { setScrollNode, scrollNode };
}
